/**
 * ProviderProfilePage — `/dashboard/provider/profile`.
 *
 * Phase 2 extraction: business_name + description + pricing
 * `provider_profile.commission_pct` was removed entirely. Commission
 * (per_dog_price + recurring_discount_pct) only.
 * Coverage moved to its own page; this page is purely "how your
 * business is described and priced". Backend PATCH deep-merges so
 * sending only these fields leaves zips/availability untouched.
 */
import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { DollarSign, Loader2 } from "lucide-react";
import { fetchTimezones, detectBrowserTimezone } from "@/lib/timezones";
import ImpersonationConfirmGate, {
  useImpersonationGate,
} from "@/components/ImpersonationConfirmGate";
import YourTierPanel from "@/components/provider/YourTierPanel";

export default function ProviderProfilePage() {
  const { setProviderProfile, setProfile } = useAuth();
  const { pricing: defaults } = useConfig();
  const { requestAction, gateProps } = useImpersonationGate();
  const [form, setForm] = useState({
    business_name: "",
    description: "",
    pricing: { per_dog_price: 0, recurring_discount_pct: 0 },
    // Track B — explicit IANA timezone on provider profile edit.
    timezone: "",
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [tierId, setTierId] = useState(null);
  const [tzList, setTzList] = useState([]);
  const [showTzNotSetHint, setShowTzNotSetHint] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/profiles/provider/me");
        if (!active) return;
        setForm({
          business_name: data?.business_name || "",
          description: data?.description || "",
          pricing: {
            per_dog_price: data?.pricing?.per_dog_price ?? 0,
            recurring_discount_pct: data?.pricing?.recurring_discount_pct ?? 0,
          },
          timezone: data?.timezone || detectBrowserTimezone(),
        });
        setShowTzNotSetHint(!data?.timezone);
        setTierId(data?.tier_id ?? null);
        setProviderProfile(data);
        setProfile(data);
        // Load timezone options in parallel (kicked off after profile
        // resolution — no need to block the profile paint on it).
        fetchTimezones().then((options) => setTzList(options));
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (active) setLoaded(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [setProviderProfile, setProfile]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  // Item 9a/9d (Feb 2026 launch-blocking batch): `updatePricing` was
  // retired alongside the editable pricing inputs. The `form.pricing`
  // subdoc is still tracked in local state (see `useState` above) so a
  // future re-introduction of partner-editable pricing (post-launch
  // floor model) can rehydrate from the server without a schema change.

  const handleSave = (e) => {
    e.preventDefault();
    requestAction("save business profile", async () => {
      setSaving(true);
      try {
        // Item 9a/9d (Feb 2026 launch-blocking batch): `pricing` is
        // intentionally OMITTED from this PATCH payload. Bookings on
        // PooPluckers use platform config ($20 base + $7/additional
        // dog) as the SOLE source of truth for launch. The dormant
        // `pricing` subdoc on `provider_profiles` is preserved on the
        // server (backend deep_merge leaves it untouched when the
        // field is absent) so the post-launch floor model can pick
        // it up without a schema change. See the read-only pricing
        // info block in the JSX below for the customer-facing copy.
        const payload = {
          business_name: form.business_name,
          description: form.description,
          timezone: form.timezone,
        };
        const { data } = await api.patch("/profiles/provider", payload);
        setProviderProfile(data);
        setProfile(data);
        setForm((s) => ({
          ...s,
          business_name: data?.business_name || "",
          description: data?.description || "",
          // Item 9a/9d: `pricing` is retained in local form state so a
          // future re-introduction of partner-editable pricing (post-
          // launch floor model) can rehydrate from the server without
          // a schema change. The field is currently NEITHER rendered
          // NOR sent — see the JSX pricing info block below and the
          // omitted `pricing` key in the PATCH payload above.
          pricing: {
            per_dog_price: data?.pricing?.per_dog_price ?? 0,
            recurring_discount_pct: data?.pricing?.recurring_discount_pct ?? 0,
          },
          timezone: data?.timezone || s.timezone,
        }));
        if (data?.timezone) setShowTzNotSetHint(false);
        toast.success("Business profile saved!");
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setSaving(false);
      }
    });
  };

  return (
    <div className="mx-auto max-w-6xl" data-testid="provider-profile-page">
      <header className="mb-6">
        <span className="text-sm font-bold uppercase tracking-wider text-brand-orange">
          Profile
        </span>
        <h2 className="font-heading text-3xl font-bold text-pp-text">
          Business profile
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Customers see this when matching with you.
        </p>
      </header>

      <Card
        className="rounded-3xl border-border bg-pp-surface p-8 shadow-none"
        data-testid="provider-profile-card"
      >
        <form
          onSubmit={handleSave}
          className="space-y-5"
          data-testid="provider-profile-form"
        >
          {!loaded && (
            <p
              className="text-sm text-muted-foreground"
              data-testid="provider-profile-loading"
            >
              Loading your business profile…
            </p>
          )}
          <fieldset disabled={!loaded} className="space-y-5 disabled:opacity-60">
            <div>
              <Label className="text-sm font-semibold text-pp-text">
                Business name
              </Label>
              <Input
                value={form.business_name}
                onChange={(e) => update("business_name", e.target.value)}
                className="mt-2 h-12 rounded-full px-5"
                placeholder="Pat's Poo Patrol"
                data-testid="provider-business-name-input"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-pp-text">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                rows={3}
                className="mt-2 rounded-2xl px-5 py-3"
                placeholder="Friendly, reliable yard cleanup…"
                data-testid="provider-description-input"
              />
            </div>

            <div
              className="rounded-2xl border border-border bg-muted p-5"
              data-testid="provider-profile-platform-pricing-info"
            >
              <p className="mb-2 flex items-center gap-2 font-heading text-lg font-semibold text-pp-text">
                <DollarSign className="h-5 w-5 text-brand-orange" />
                Pricing
              </p>
              <p
                className="text-sm text-pp-text"
                data-testid="provider-profile-platform-pricing-copy"
              >
                Pricing is set by PooPluckers: $20 base + $7 per additional dog.
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Bookings on the platform use this rate everywhere. Your own
                direct business and its pricing stay entirely yours.
              </p>
            </div>

            {/* Track B — timezone edit field. Pre-fills from backend
                if set, else browser-detected. Legacy-cohort hint
                surfaces the "not yet set — please confirm" prompt. */}
            <div data-testid="provider-profile-tz-block">
              <Label htmlFor="provider-tz-select" className="text-sm font-semibold text-pp-text">
                Timezone
              </Label>
              <select
                id="provider-tz-select"
                data-testid="provider-profile-tz-select"
                value={form.timezone}
                onChange={(e) => update("timezone", e.target.value)}
                className="pp-select mt-2 h-11 w-full rounded-full border px-5 text-sm"
              >
                {!form.timezone && <option value="">— pick a timezone —</option>}
                {tzList.map((z) => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </select>
              {showTzNotSetHint ? (
                <p
                  className="mt-1.5 text-xs text-pp-orange"
                  data-testid="provider-profile-tz-hint"
                >
                  Not yet set — please confirm. We auto-detected your
                  browser's timezone; change it if needed.
                </p>
              ) : (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Used to schedule your booking reminders at the right local time.
                </p>
              )}
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={saving || !loaded}
                className="h-12 rounded-full bg-pp-orange px-8 text-pp-cta-text hover:bg-pp-orange-hover"
                data-testid="provider-save-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save profile"
                )}
              </Button>
            </div>
          </fieldset>
        </form>
      </Card>
      <YourTierPanel currentTierId={tierId} />
      <ImpersonationConfirmGate {...gateProps} />
    </div>
  );
}
